// Read-only detail view for a variation. Mirrors SowDetailExpand: <dl>
// label/value layout, status pill, signed value-impact cell, clickable
// evidence links, an Edit button that the parent wires to swap the
// expanded row from view-mode to edit-mode (VariationInlineExpand).
// Number is shown as a quiet monospace pill for visual parity with the
// SOW number display.
//
// Multi-target: the Targets row lists every entry from `targets[]` with
// its optional allocation. Read path uses `readTargets` so legacy
// records without `targets` synthesise a single-element list from the
// deprecated `variesType` + `variesId` fields.

import { Pencil, ExternalLink } from "lucide-react";
import { safeExternalHref } from "../../../../utils/evidenceLinks";
import Button from "../../../common/Button";
import VariationStatusPill from "./VariationStatusPill";
import ValueImpactCell from "./ValueImpactCell";
import { readTargets } from "./variationTargets";
import { gstSuffix } from "../../../../utils/gstSuffix";
import type {
  Sow,
  Variation,
  VariationTarget,
} from "../../../../types";

const FORMATTER = new Intl.NumberFormat("en-AU", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function fmtAud(n: number): string {
  const sign = n < 0 ? "-" : "";
  return `${sign}$${FORMATTER.format(Math.abs(n))}`;
}

function targetLabel(
  t: VariationTarget,
  sowsById: Map<string, Sow>,
): string {
  if (t.kind === "contract") return "Contract";
  const sow = sowsById.get(t.sowId);
  return sow ? `${sow.number} · ${sow.name}` : t.sowId;
}

interface VariationDetailExpandProps {
  item: Variation;
  gstApplies: boolean | undefined;
  /** Used to resolve the SOW name when variesType is `sow`. */
  sowsById: Map<string, Sow>;
  onEdit?: () => void;
  /** Open the editor-gated multi-field correction modal. Bypasses the
   *  standard PUT path's status-gate so an Approved or Rejected
   *  variation can be repaired for typos. Passing undefined hides
   *  the trigger entirely. */
  onCorrect?: () => void;
}

function fmtDate(v?: string): string {
  if (!v) return "—";
  try {
    return new Date(`${v}T00:00:00`).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return v;
  }
}

function DL({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <dt className="text-caption text-slate-500 dark:text-slate-400">
        {label}
      </dt>
      <dd className="mt-1 text-body text-slate-900 dark:text-slate-100">
        {children}
      </dd>
    </div>
  );
}

export default function VariationDetailExpand({
  item,
  gstApplies,
  sowsById,
  onEdit,
  onCorrect,
}: VariationDetailExpandProps) {
  const targets = readTargets(item);
  const basis = gstSuffix(gstApplies);

  return (
    <section
      data-testid="variation-detail-expand"
      className="space-y-5 p-4"
      aria-label={`Details for ${item.name}`}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <h3 className="text-h3 text-slate-900 dark:text-slate-100">
            {item.name}
          </h3>
          <div className="flex flex-wrap items-center gap-2">
            <VariationStatusPill status={item.status} />
            <span
              className="font-mono text-caption text-slate-500 dark:text-slate-400"
              data-testid="variation-detail-number"
            >
              {item.number}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onCorrect && (
            <Button
              variant="secondary"
              size="sm"
              onClick={onCorrect}
              data-testid="variation-detail-correct"
            >
              Correct
            </Button>
          )}
          {onEdit && (
            <Button
              variant="secondary"
              size="sm"
              icon={<Pencil className="h-4 w-4" />}
              onClick={onEdit}
              data-testid="variation-detail-edit"
            >
              Edit
            </Button>
          )}
        </div>
      </header>

      <DL label="Targets">
        <ul
          className="space-y-0.5"
          data-testid="variation-detail-targets"
        >
          {targets.map((t, i) => {
            const allocated =
              t.allocation !== null && t.allocation !== undefined;
            return (
              <li
                key={
                  t.kind === "contract" ? "contract" : `${t.sowId}-${i}`
                }
              >
                <span>{targetLabel(t, sowsById)}</span>
                {allocated && (
                  <span className="ml-2 text-caption text-slate-500 dark:text-slate-400">
                    · {fmtAud(t.allocation as number)} allocated ({basis})
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </DL>
      <dl className="flex flex-wrap gap-x-10 gap-y-4">
        <DL label="Effective date">{fmtDate(item.effectiveDate)}</DL>
        <DL label="Value impact">
          <ValueImpactCell
            value={item.valueImpact}
            gstApplies={gstApplies}
            testId="variation-detail-value-impact"
          />
        </DL>
      </dl>
      {item.description && (
        <DL label="Description">
          <span className="whitespace-pre-wrap">{item.description}</span>
        </DL>
      )}

      {item.evidenceLinks && item.evidenceLinks.length > 0 && (
        <div>
          <h4 className="text-caption text-slate-500 dark:text-slate-400">
            Evidence links
          </h4>
          <ul className="mt-2 space-y-1">
            {item.evidenceLinks.map((link, i) => {
              const href = safeExternalHref(link.url);
              const text = link.label || link.url;
              return (
                <li key={i}>
                  {href ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-body text-brand-600 hover:text-brand-500 dark:text-brand-400 hover:underline"
                    >
                      {text}
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  ) : (
                    <span className="text-body text-slate-700 dark:text-slate-300">
                      {text}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {item.notes && (
        <div>
          <h4 className="text-caption text-slate-500 dark:text-slate-400">
            Notes
          </h4>
          <p className="mt-1 text-body whitespace-pre-wrap text-slate-900 dark:text-slate-100">
            {item.notes}
          </p>
        </div>
      )}

      {item.status === "rejected" && item.rejectedReason && (
        <div>
          <h4 className="text-caption text-slate-500 dark:text-slate-400">
            Rejection reason
          </h4>
          <p
            className="mt-1 text-body whitespace-pre-wrap text-slate-900 dark:text-slate-100"
            data-testid="variation-detail-rejected-reason"
          >
            {item.rejectedReason}
          </p>
        </div>
      )}
    </section>
  );
}
